import type { DeviceElement } from "../scene/schema";
import { deviceCanRotate, deviceModel, suggestDevice } from "./catalog";
import { type DeviceProps, deviceAspect, deviceExtent } from "./layout";

/**
 * Element-level helpers for device mockups (docs/19-mockups.md): box sizes
 * when a device is inserted or its view changes, and what a screenshot on the
 * screen changes. Shared by the editor and the MCP tools.
 */

export interface PixelSize {
  width?: number | null | undefined;
  height?: number | null | undefined;
}

/** Height / width of a screenshot (null without known size). */
export function deviceContentAspect(size: PixelSize | null | undefined): number | null {
  return size?.width && size.height ? size.height / size.width : null;
}

/** Box of a device that fits into `max` (the device's own aspect ratio). */
export function deviceBoxSize(
  props: DeviceProps,
  contentAspect: number | null,
  max: { width: number; height: number },
): { width: number; height: number } {
  const aspect = deviceAspect(props, contentAspect);
  const width = Math.min(max.width, max.height * aspect);
  return { width: Math.round(width), height: Math.round(width / aspect) };
}

type Box = { x: number; y: number; width: number; height: number };

function centred(box: Box, width: number, height: number): Box {
  return {
    x: box.x + (box.width - width) / 2,
    y: box.y + (box.height - height) / 2,
    width,
    height,
  };
}

/**
 * New box after the view changes (tilt, orientation, colour, address, the
 * shape of a browser page): same centre and the device keeps its size on the
 * page. A new model instead takes the old box's longer side.
 */
export function resizeDeviceBox(
  before: DeviceElement,
  after: DeviceProps,
  contentBefore: number | null,
  contentAfter: number | null,
): Box {
  const box = { x: before.x, y: before.y, width: before.width, height: before.height };
  if (after.device !== before.device) {
    const aspect = deviceAspect(after, contentAfter);
    const side = Math.max(box.width, box.height);
    const width = aspect >= 1 ? side : side * aspect;
    return centred(box, width, width / aspect);
  }
  const old = deviceExtent(before, contentBefore);
  // Pixels per millimetre the device has now (it is fitted into its box).
  const scale = Math.min(box.width / old.width, box.height / old.height);
  const next = deviceExtent(after, contentAfter);
  return centred(box, next.width * scale, next.height * scale);
}

/**
 * Puts a screenshot on the screen. Phones and tablets turn to the picture's
 * orientation, browser windows take its shape; the box follows (same centre
 * and scale). Mutates the element (works on drafts).
 */
export function applyDeviceScreenshot(
  element: DeviceElement,
  assetId: string,
  size: PixelSize | null | undefined,
  previous?: PixelSize | null,
): void {
  const model = deviceModel(element.device);
  const before: DeviceElement = { ...element, tilt: { ...element.tilt } };
  element.assetId = assetId;
  if (size?.width && size.height && deviceCanRotate(model)) {
    const nativeWide = model.screen.width > model.screen.height;
    const wide = size.width > size.height;
    if (size.width !== size.height) element.landscape = wide !== nativeWide;
  }
  Object.assign(
    element,
    resizeDeviceBox(before, element, deviceContentAspect(previous), deviceContentAspect(size)),
  );
}

/** Device for a screenshot (by its size) in the orientation of the picture. */
export function deviceForScreenshot(size: PixelSize | null | undefined): {
  device: string;
  landscape: boolean;
} {
  return suggestDevice(size?.width, size?.height);
}
